import { z } from 'zod';
import { payorSchema } from './payor';
import { siteSchema } from './site';

const QDOC_SITE_NAME = 'QDoc Virtual Healthcare';
const BC_PROVINCE_CODE = 'BC';
const SK_PROVINCE_CODE = 'SK';

export type LocationOfServiceOption = {
  label: string;
  value: string;
};

export const MB_LOCATION_OF_SERVICE_OPTIONS = [
  { label: 'Brandon', value: 'B' },
  { label: 'Northern', value: 'N' },
  { label: 'Rural', value: 'R' },
  { label: 'Remote', value: 'C' },
  { label: 'Winnipeg', value: 'W' },
  { label: 'Other', value: 'O' },
] as const satisfies readonly LocationOfServiceOption[];

export const BC_LOCATION_OF_SERVICE_OPTIONS = [
  {
    label:
      "A – Practitioner's Office – In Community (only available until September 30, 2021)",
    value: 'A',
  },
  { label: 'B – Community Health Centre', value: 'B' },
  {
    label: 'C – Residential Care/Assisted Living Residence',
    value: 'C',
  },
  { label: 'D – Diagnostic Facility', value: 'D' },
  {
    label: 'E – Hospital Emergency Room (unscheduled patient)',
    value: 'E',
  },
  { label: 'F – Private medical/surgical facility', value: 'F' },
  { label: 'G – Hospital, Day Care (surgery)', value: 'G' },
  { label: 'I – Hospital Inpatient', value: 'I' },
  {
    label: 'J – First Nations Primary Health Care Clinic',
    value: 'J',
  },
  { label: 'K – Hybrid Primary Care Practice', value: 'K' },
  {
    label:
      'L – Longitudinal Primary Care Practice (e.g. GP family practice or PCN clinic)',
    value: 'L',
  },
  { label: 'M – Mental Health Centre', value: 'M' },
  {
    label: 'N – Health Care Practitioner Office (non-physician)',
    value: 'N',
  },
  { label: 'P – Hospital Outpatient', value: 'P' },
  { label: 'Q – Specialist Physician Office', value: 'Q' },
  { label: "R – Patient's private home", value: 'R' },
  {
    label: "T – Practitioner's office, in publicly administered facility",
    value: 'T',
  },
  { label: 'U – Urgent and Primary Care Centre', value: 'U' },
  { label: 'V – Exclusive Virtual Care Clinic', value: 'V' },
  { label: 'W – Walk-In Clinic', value: 'W' },
  {
    label: 'Z – Other, e.g., accident site or in an ambulance',
    value: 'Z',
  },
] as const satisfies readonly LocationOfServiceOption[];

export const SK_LOCATION_OF_SERVICE_OPTIONS = [
  { label: '1', value: '1' },
  { label: '2', value: '2' },
  { label: '3', value: '3' },
  { label: '4', value: '4' },
  { label: '5', value: '5' },
  { label: '9', value: '9' },
  { label: 'B', value: 'B' },
  { label: 'C', value: 'C' },
  { label: 'D', value: 'D' },
  { label: 'E', value: 'E' },
  { label: 'F', value: 'F' },
  { label: 'K', value: 'K' },
  { label: 'M', value: 'M' },
  { label: 'P', value: 'P' },
  { label: 'T', value: 'T' },
] as const satisfies readonly LocationOfServiceOption[];

export const BC_LOCATION_OF_SERVICE_REQUIRED_MESSAGE =
  'Location of Service is required for claims with a BC payor';

export const BC_LOCATION_OF_SERVICE_INVALID_MESSAGE =
  'Location of Service must be a valid BC location code';

export const SK_LOCATION_OF_SERVICE_INVALID_MESSAGE =
  'Location of Service must be a valid SK location code';

export const NON_SK_LOCATION_OF_SERVICE_INVALID_MESSAGE =
  'Location of Service must be a valid provincial letter code';

export const QDOC_LOCATION_OF_SERVICE_REQUIRED_MESSAGE =
  'Location of Service is required for QDoc Virtual Healthcare claims';

type ClaimLocationOfServiceData = {
  locationOfService?: string | null;
  payor?: z.infer<typeof payorSchema> | null;
  site?: z.infer<typeof siteSchema> | null;
};

type PayorProvince = Pick<z.infer<typeof payorSchema>, 'province'>;

const bcLocationOfServiceCodes = new Set<string>(
  BC_LOCATION_OF_SERVICE_OPTIONS.map(({ value }) => value)
);
const skLocationOfServiceCodes = new Set<string>(
  SK_LOCATION_OF_SERVICE_OPTIONS.map(({ value }) => value)
);

export const isBCPayor = (payor: PayorProvince | null | undefined): boolean =>
  payor?.province?.trim().toUpperCase() === BC_PROVINCE_CODE;

export const isSKPayor = (payor: PayorProvince | null | undefined): boolean =>
  payor?.province?.trim().toUpperCase() === SK_PROVINCE_CODE;

export const getLocationOfServiceOptions = (
  payor: PayorProvince | null | undefined
): readonly LocationOfServiceOption[] => {
  if (isBCPayor(payor)) {
    return BC_LOCATION_OF_SERVICE_OPTIONS;
  }
  if (isSKPayor(payor)) {
    return SK_LOCATION_OF_SERVICE_OPTIONS;
  }
  return MB_LOCATION_OF_SERVICE_OPTIONS;
};

export const validateClaimLocationOfService = (
  data: ClaimLocationOfServiceData,
  ctx: z.RefinementCtx
): void => {
  const locationOfService = data.locationOfService?.trim();

  if (isBCPayor(data.payor)) {
    if (!locationOfService) {
      ctx.addIssue({
        code: 'custom',
        message: BC_LOCATION_OF_SERVICE_REQUIRED_MESSAGE,
        path: ['locationOfService'],
      });
      return;
    }

    if (!bcLocationOfServiceCodes.has(locationOfService)) {
      ctx.addIssue({
        code: 'custom',
        message: BC_LOCATION_OF_SERVICE_INVALID_MESSAGE,
        path: ['locationOfService'],
      });
    }
    return;
  }

  if (isSKPayor(data.payor)) {
    if (locationOfService && !skLocationOfServiceCodes.has(locationOfService)) {
      ctx.addIssue({
        code: 'custom',
        message: SK_LOCATION_OF_SERVICE_INVALID_MESSAGE,
        path: ['locationOfService'],
      });
    }
    return;
  }

  if (
    data.payor?.province &&
    locationOfService &&
    !/^[A-Z]$/.test(locationOfService)
  ) {
    ctx.addIssue({
      code: 'custom',
      message: NON_SK_LOCATION_OF_SERVICE_INVALID_MESSAGE,
      path: ['locationOfService'],
    });
    return;
  }

  if (locationOfService) {
    return;
  }

  if (data.site?.name === QDOC_SITE_NAME) {
    ctx.addIssue({
      code: 'custom',
      message: QDOC_LOCATION_OF_SERVICE_REQUIRED_MESSAGE,
      path: ['locationOfService'],
    });
  }
};
